import {
  ref, onMounted, isRef, watch,
} from 'vue';
import api from '../../api/api';
import { urlParamsFormatter } from '../utility/string';
import { NUMBER_OF_CHARACTERS_TO_SEARCH } from '../../components/utility/constants';
import isFullUrl from '../utility/url';

/**
 * Get queries from url
 * If you want to pick some initial url params
 * @returns Object
 */
const formatUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const entries = urlParams.entries();
  let params = {};
  for (const entry of entries) {
    params = {
      ...params,
      [entry[0]]: entry[1],
    };
  }
  return params;
};

export default (endpoint, filterObj, onMountBool = true, hitByPage = 8, analitycs = null) => {
  const listItems = ref(null);
  const restFromResponse = ref(null);
  const isLoading = ref(false);
  const firstLoad = ref(false);
  const currentPage = ref(null);
  const filters = ref(filterObj || '');
  let debounceTime = null;
  const onMount = ref(onMountBool);
  const watchFilterChange = ref(true);
  const offset = ref(0);
  const hit = ref(hitByPage);
  const availableFilters = ref(null);
  const analitycsData = ref(analitycs);
  const endpointUrl = ref('');

  // Methodes
  /**
   * Fetch results with endpoint and filters
   */
  const fetchResults = async () => {
    if (endpointUrl.value === '') {
      endpointUrl.value = isRef(endpoint) ? endpoint.value : endpoint;
    }
    // Check if the relative path contains query parameters
    const queryIndex = endpointUrl.value.indexOf('?');

    const params = {
      // initial url params
      ...formatUrlParams(),
      // prop params
      page: currentPage.value,
      itemsPerPage: hit.value,
      // overrides from components after interaction
      ...filters.value,
      // keep offset at bottom so category and pagination can control behaviour
      offset: offset.value,
    };
    if (queryIndex !== -1) {
      let urlObj = null;
      if (isFullUrl(endpointUrl.value)) {
        urlObj = new URL(endpointUrl.value);
      } else {
        urlObj = new URL(endpointUrl.value, window.location.origin);
      }

      const searchParams = new URLSearchParams(urlObj.search);
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
      urlObj.search = '';

      endpointUrl.value = urlObj.toString();
    }
    clearTimeout(debounceTime);
    debounceTime = setTimeout(async () => {
      try {
        isLoading.value = true;
        const url = urlParamsFormatter(endpointUrl.value, params);
        const response = await api.get(url);
        const { items, ...restData } = response.data;
        listItems.value = items;
        restFromResponse.value = { ...restData };
        isLoading.value = false;
        availableFilters.value = response.data.availableFilters;
        setTimeout(() => {
          const loader = window.document.querySelector('.js-list-on-load');
          if (loader) {
            loader.remove();
          }
          // Remove style attribute
          const appElement = document.getElementById('app');
          if (appElement.hasAttribute('style')) {
            appElement.removeAttribute('style');
          }
        }, 400);
        if (!firstLoad.value) {
          firstLoad.value = true;
        }

        if (
          analitycsData.value
          && response.data?.query?.queryText.length >= NUMBER_OF_CHARACTERS_TO_SEARCH
        ) {
          const obj = {
            ...analitycsData.value,
            ga4_data: {
              search_term: response.data.query.queryText,
              search_results_count: response.data.total,
            },
          };
          // GA4 view search event
          window.dataLayer.push(obj);
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        isLoading.value = false;
        // Error handler
      }
    }, 400);
  };

  /**
   * Pagination callback to fetch results from certain page
   * @param {Number} page - page number
   */
  const pageChangedHanlder = (page) => {
    currentPage.value = page;
    offset.value = (hit.value * page) - hit.value;
    fetchResults();
  };

  const getFilters = () => filters.value;

  /**
   * Update filters
   * @param {Object} - key/val of filter or filters
   * @param {Bool} watchFilters - support for trigger or not api calls
   * @returns Function that returns updated filters
   */
  const onFilterChanged = (filterObject, watchFilters = true) => {
    watchFilterChange.value = watchFilters;
    if (filterObject
      && Object.keys(filterObject).length !== 0
      && Object.getPrototypeOf(filterObject) === Object.prototype) {
      filters.value = {
        ...filters.value,
        ...filterObject,
      };
      currentPage.value = null;
      offset.value = 0;
    }

    return getFilters;
  };

  /**
   * Clear query filter
   */
  const clearQueryFilter = () => {
    if (filters.value && filters.value.query) {
      filters.value.query = '';
    }
  };

  // Lifecycle
  /**
   * Get results from api call when component mount
   */
  onMounted(() => {
    if (onMount.value) {
      fetchResults();
    }
  });

  /**
   * Watch filter changes and fetch new results
   */
  if (isRef(filters)) {
    watch(
      filters,
      () => {
        if (watchFilterChange.value) {
          fetchResults();
        }
      },
      { deep: true },
    );
  }

  return {
    listItems,
    restFromResponse,
    isLoading,
    filters,
    currentPage,
    availableFilters,
    firstLoad,
    pageChangedHanlder,
    onFilterChanged,
    clearQueryFilter,
  };
};
